import {
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
  TextInput,
} from 'platform/components';
import {Box, VStack, HStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';
import {useSelector} from 'react-redux';

import {isString} from 'ramda-adjunct';

import {
  useGetPartDepartmentSettingDataQuery,
  useGetPartDepartmentTypesQuery,
  usePostPartDepartmentInsertMutation,
} from '@dms/api/core';
import {selectActiveBranchId} from '@dms/api/features';
import {useGetServiceOrderTypesQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {
  SettingsFooter,
  SettingsSection,
  SettingsTemplate,
  SettingsTemplateHeader,
} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {EMPTY_PLACEHOLDER, useNavigate} from 'shared/web';

type FormType = {
  branchId: string;
  serviceOrderTypes: {
    serviceOrderTypeId: string;
    departmentTypeId: string;
  }[];
};

export function PartDepartment() {
  const navigate = useNavigate();
  const branchId = useSelector(selectActiveBranchId);
  const [currentBranchId, setCurrentBranchId] = useState(branchId);
  const {branchOptions, isLoading: isLoadingBranches, isError: isErrorBranches} = useBranches();

  const {
    data: serviceOrderTypes = [],
    isLoading: isLoadingServiceOrderTypes,
    isError: isErrorServiceOrderTypes,
  } = useGetServiceOrderTypesQuery();

  const {
    data: partDepartmentTypes = [],
    isLoading: isLoadingPartDepartmentTypes,
    isError: isErrorPartDepartmentTypes,
  } = useGetPartDepartmentTypesQuery();

  const {
    data: settingData,
    isLoading: isLoadingSettingData,
    refetch,
    isError: isErrorSettingData,
  } = useGetPartDepartmentSettingDataQuery(
    {branchId: currentBranchId},
    {skip: !isString(currentBranchId)}
  );

  const [postPartDepartmentInsert, {isLoading: isLoadingPostPartDepartmentInsert}] =
    usePostPartDepartmentInsertMutation();

  const partDepartmentTypeOptions = partDepartmentTypes.map((type) => ({
    label: type.departmentTypeName,
    value: type.departmentTypeId,
  }));

  const handleSubmit: FormSubmitHandler<FormType> = async (values) => {
    const payload = values.serviceOrderTypes.map((type) => ({
      branchId: values.branchId,
      serviceOrderTypeId: type.serviceOrderTypeId,
      departmentTypeId: type.departmentTypeId,
    }));

    await postPartDepartmentInsert({body: payload})
      .unwrap()
      .then(() => {
        showNotification.success();
        refetch();
      })
      .catch(handleApiError);
  };

  const handleBranchChange = (newBranchId: string) => {
    setCurrentBranchId(newBranchId);
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.partDepartment.title'),
    breadcrumbs: [
      {
        label: i18n.t('page.settings.labels.mobisIris'),
      },
      {
        label: i18n.t('entity.partDepartment.title'),
        href: settingsRoutes.partDepartment,
      },
    ],
  };

  const defaultDepartmentTypeId =
    partDepartmentTypes.find((type) => type.departmentTypeName === 'Other')?.departmentTypeId ??
    partDepartmentTypes[0]?.departmentTypeId;

  const initialData = {
    branchId: currentBranchId,
    serviceOrderTypes: serviceOrderTypes.map((type) => ({
      serviceOrderTypeId: type?.id ?? '',
      departmentTypeId:
        settingData?.find((item) => item.service_order_type_id === type?.id)?.department_type_id ??
        defaultDepartmentTypeId,
    })),
  };

  const isDataLoading =
    isLoadingServiceOrderTypes ||
    isLoadingPartDepartmentTypes ||
    isLoadingSettingData ||
    isLoadingBranches;

  const isDataError =
    isErrorServiceOrderTypes || isErrorPartDepartmentTypes || isErrorSettingData || isErrorBranches;

  return (
    <SettingsTemplate header={header} data-testid={testIds.settings.partDepartment('page')}>
      <DataStatus isLoading={isDataLoading} isError={isDataError}>
        <Form<FormType>
          experimentalZodSchema={schema}
          onSubmit={handleSubmit}
          defaultValues={initialData}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <>
              <SettingsSection>
                <VStack spacing={4}>
                  <Box width={70}>
                    <DataStatus isLoading={isLoadingBranches}>
                      <FormField
                        control={control}
                        name="branchId"
                        type="choice"
                        options={branchOptions}
                        label={i18n.t('entity.branch.labels.branch')}
                        isRequired
                        isNotClearable
                        onChange={(value) => handleBranchChange(value as string)}
                        data-testid={testIds.settings.partDepartment('branch')}
                      />
                    </DataStatus>
                  </Box>
                  <Separator />
                  {serviceOrderTypes.map((type, index) => (
                    <HStack key={type?.id} width="100%" spacing={4} align="flex-end">
                      <Box flex={1}>
                        <TextInput
                          type="text"
                          label={
                            index === 0
                              ? i18n.t('entity.partDepartment.labels.serviceOrderType')
                              : undefined
                          }
                          value={type?.name ?? EMPTY_PLACEHOLDER}
                          isDisabled
                          data-testid={testIds.settings.partDepartment(`serviceOrderType-${index}`)}
                        />
                      </Box>
                      <Box flex={1}>
                        <FormField
                          control={control}
                          name={`serviceOrderTypes.${index}.departmentTypeId`}
                          type="choice"
                          options={partDepartmentTypeOptions}
                          label={
                            index === 0
                              ? i18n.t('entity.partDepartment.labels.partDepartmentType')
                              : undefined
                          }
                          isRequired
                          isNotClearable
                          data-testid={testIds.settings.partDepartment(
                            `partDepartmentType-${index}`
                          )}
                        />
                      </Box>
                    </HStack>
                  ))}
                </VStack>
              </SettingsSection>
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    variant: 'secondary',
                    title: i18n.t('general.actions.discard'),
                    onClick: () => navigateWithoutBlocker(settingsRoutes.partDepartment),
                    'data-testid': testIds.settings.partDepartment('discard'),
                  },
                  {
                    control,
                    type: 'form-button',
                    buttonType: 'submit',
                    variant: 'primary',
                    title: i18n.t('general.actions.saveChanges'),
                    isLoading: isLoadingPostPartDepartmentInsert,
                    'data-testid': testIds.settings.partDepartment('saveChanges'),
                  },
                ]}
              />
            </>
          )}
        </Form>
      </DataStatus>
    </SettingsTemplate>
  );
}

const schema = z.object({
  branchId: z.string().min(1, i18n.t('general.errors.required')),
  serviceOrderTypes: z
    .array(
      z.object({
        serviceOrderTypeId: z.string().min(1, i18n.t('general.errors.required')),
        departmentTypeId: z.string().min(1, i18n.t('general.errors.required')),
      })
    )
    .min(1, i18n.t('general.errors.required')),
});
